import { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { SelectPopover } from '../../components/ui/select-popover';

export type ColorCategory = 'primary' | 'secondary' | 'accent' | 'neutral';

export interface ColorEntry {
	name: string;
	hex: string;
	usage: string;
	category: ColorCategory;
}

const categoryOptions = [
	{ value: 'primary', label: 'Primary' },
	{ value: 'secondary', label: 'Secondary' },
	{ value: 'accent', label: 'Accent' },
	{ value: 'neutral', label: 'Neutral' },
];

function hexToRgb(hex: string): { r: number; g: number; b: number } {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: parseInt(result[1], 16),
				g: parseInt(result[2], 16),
				b: parseInt(result[3], 16),
			}
		: { r: 0, g: 0, b: 0 };
}

function rgbToHex(r: number, g: number, b: number): string {
	return (
		'#' +
		[r, g, b]
			.map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0'))
			.join('')
			.toUpperCase()
	);
}

export function AddColorModal({
	open,
	onOpenChange,
	onSave,
	loading,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSave: (color: ColorEntry) => void;
	loading?: boolean;
}) {
	const [name, setName] = useState('');
	const [hex, setHex] = useState('#5E3131');
	const [usage, setUsage] = useState('');
	const [category, setCategory] = useState<ColorCategory>('primary');

	const resetForm = () => {
		setName('');
		setHex('#5E3131');
		setUsage('');
		setCategory('primary');
	};

	const handleSubmit = () => {
		if (!name.trim() || !isValidHex) return;
		onSave({
			name: name.trim(),
			hex: hex.trim().toUpperCase(),
			usage: usage.trim(),
			category,
		});
		resetForm();
	};

	const handleHexInput = (value: string) => {
		if (value && !value.startsWith('#')) {
			setHex('#' + value);
		} else {
			setHex(value);
		}
	};

	const isValidHex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
	const rgb = hexToRgb(isValidHex ? hex : '#000000');

	const handleRgbChange = (channel: 'r' | 'g' | 'b', value: string) => {
		const num = value === '' ? 0 : parseInt(value, 10);
		if (isNaN(num)) return;
		const clamped = Math.max(0, Math.min(255, num));
		const next = { ...rgb, [channel]: clamped };
		setHex(rgbToHex(next.r, next.g, next.b));
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-[380px]">
				<DialogHeader>
					<DialogTitle>Add color</DialogTitle>
					<DialogDescription>Add a color to the brand palette.</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-5">
							{/* Color picker */}
							<div className="brand-color-picker">
								<HexColorPicker
									color={isValidHex ? hex : '#000000'}
									onChange={(c) => setHex(c.toUpperCase())}
								/>
							</div>

							{/* Swatch + Hex + RGB inputs */}
							<div className="flex items-start gap-3">
								<div
									className="mt-0.5 h-10 w-10 shrink-0 rounded-[var(--r-3)] border border-line-2"
									style={{ backgroundColor: isValidHex ? hex : '#000' }}
								/>
								<div className="grid flex-1 grid-cols-[1fr_auto] gap-x-3 gap-y-0">
									<div>
										<Input
											value={hex}
											onChange={(e) => handleHexInput(e.target.value)}
											placeholder="#CC0000"
											className="font-mono text-sm"
										/>
									</div>
									<div className="flex items-center gap-1.5">
										{(['r', 'g', 'b'] as const).map((ch) => (
											<div key={ch} className="w-11">
												<Input
													type="number"
													min={0}
													max={255}
													value={rgb[ch]}
													onChange={(e) =>
														handleRgbChange(ch, e.target.value)
													}
													className="text-center font-mono text-sm"
												/>
											</div>
										))}
									</div>
									<div className="mt-1 text-2xs font-medium uppercase tracking-wider text-text-4">
										Hex
									</div>
									<div className="mt-1 flex gap-1.5">
										{['R', 'G', 'B'].map((label) => (
											<div
												key={label}
												className="w-11 text-center text-2xs font-medium uppercase tracking-wider text-text-4"
											>
												{label}
											</div>
										))}
									</div>
								</div>
							</div>

							<Field label="Color name" required>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Primary Red"
								/>
							</Field>

							<Field label="Category" required>
								<SelectPopover
									value={category}
									onChange={(v) => setCategory(v as ColorCategory)}
									options={categoryOptions}
									placeholder="Select category"
								/>
							</Field>

							<Field label="Usage">
								<Input
									value={usage}
									onChange={(e) => setUsage(e.target.value)}
									placeholder="e.g. Brand mark, primary surfaces"
								/>
							</Field>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || !isValidHex || loading}>
							{loading ? 'Adding...' : 'Add color'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
